import { useState, useRef, useEffect, useMemo, type KeyboardEvent } from 'react'
import { Button } from '@/components/ui/button'
import { Send, Loader2, AlertCircle, Sparkles, Wand2, ChevronDown, Coins, Wrench } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useChatUsage, useChatModels, useToolCatalog, useToolDefaults, useConversationTools, useAddConversationTools } from '@/lib/api/hooks'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { McpServerToggle } from './McpServerToggle'
import { ToolSelectorDialog } from './ToolSelectorDialog'
import { useChatStore } from '@/store/chat/chatStore'
import { toast } from 'sonner'

// Example prompts to help users discover MCP tools
const EXAMPLE_PROMPTS = [
  {
    label: 'Search activities',
    prompt: 'Search for electricity production activities in ecoinvent',
    tooltip:
      'Uses search_activities tool to find processes in the Ecoinvent database by name, location, or keywords.',
  },
  {
    label: 'Calculate impact',
    prompt: 'Calculate the carbon footprint of 1 kWh of electricity from coal',
    tooltip:
      'Uses calculate_standard tool to perform a full LCA calculation using IPCC climate change methods.',
  },
  {
    label: 'Compare products',
    prompt: 'Compare the environmental impact of paper vs plastic bags',
    tooltip:
      'Combines multiple searches and calculations to compare environmental profiles across impact categories.',
  },
  {
    label: 'Explore methods',
    prompt: 'What impact assessment methods are available?',
    tooltip:
      'Uses list_method_categories and search_methods tools to browse 45+ impact assessment method families.',
  },
]

interface MessageInputProps {
  onSend: (message: string) => void
  disabled?: boolean
  placeholder?: string
  showSuggestions?: boolean
  selectedModel: string | null
  onModelChange: (model: string | null) => void
}

export function MessageInput({
  onSend,
  disabled = false,
  placeholder = 'Message...',
  showSuggestions = false,
  selectedModel,
  onModelChange,
}: MessageInputProps) {
  const [input, setInput] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [toolsOpen, setToolsOpen] = useState(false)
  const currentConversationId = useChatStore((s) => s.currentConversationId)
  const pendingEnabledTools = useChatStore((s) => s.pendingEnabledTools)
  const setPendingEnabledTools = useChatStore((s) => s.setPendingEnabledTools)
  const { data: catalog } = useToolCatalog()
  const { data: defaults } = useToolDefaults()
  const { data: convTools } = useConversationTools(currentConversationId)
  const addConversationTools = useAddConversationTools(currentConversationId)

  // Under the conversation-on-first-message rule, currentConversationId is set
  // only after the first message completes, so an existing id always means the
  // conversation is started (add-only).
  const addOnly = !!currentConversationId
  const allNames = useMemo(() => (catalog?.tools ?? []).map((t) => t.name), [catalog])
  // Selector pre-fill:
  // - existing conversation -> its stored set (null => legacy/all => show all)
  // - no conversation yet -> the pending selection if the user already picked one
  //   this session, else the user's default template.
  // Memoized so the dialog's `value` prop is a stable ref — a background refetch
  // while the dialog is open must not refire its sync effect and wipe toggles.
  const selectorValue = useMemo(
    () =>
      currentConversationId
        ? convTools?.enabled_tools ?? allNames
        : pendingEnabledTools ?? defaults?.enabled_tools ?? [],
    [currentConversationId, convTools, allNames, pendingEnabledTools, defaults],
  )

  const handleToolsSave = (enabled: string[]) => {
    // Conversation already started: add-only — PATCH just the newly added names.
    if (currentConversationId) {
      const current = new Set(convTools?.enabled_tools ?? allNames)
      const toAdd = enabled.filter((n) => !current.has(n))
      if (toAdd.length > 0) addConversationTools.mutate(toAdd)
      return
    }
    // No conversation yet: stash the chosen set; it rides the first message and the
    // backend creates the conversation with exactly these tools (can be [] or a
    // subset smaller than the default — this is the "configure before starting" path).
    setPendingEnabledTools(enabled)
  }

  // Check usage limits
  const { data: usageData } = useChatUsage()
  const isAtLimit =
    usageData && !usageData.is_unlimited && usageData.remaining !== null && usageData.remaining <= 0

  // Get available models
  const { data: models } = useChatModels()
  const currentModel =
    models?.find((m) => m.model_id === selectedModel) || models?.find((m) => m.is_default)

  // Set default model on first load
  useEffect(() => {
    if (models && !selectedModel) {
      const defaultModel = models.find((m) => m.is_default)
      if (defaultModel) {
        onModelChange(defaultModel.model_id)
      }
    }
  }, [models, selectedModel, onModelChange])

  const handleSubmit = () => {
    if (isAtLimit) {
      // Don't allow sending if at limit
      return
    }

    if (input.trim() && !disabled) {
      onSend(input.trim())
      setInput('')
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  // Auto-resize textarea
  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value)

    // Auto-resize
    const textarea = e.target
    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
  }

  return (
    <div className="rounded-2xl border border-border/60 bg-background/95 backdrop-blur-sm shadow-lg">
      <div className="w-full">
        {/* Suggestion buttons - show when no messages or explicitly enabled */}
        {showSuggestions && !isAtLimit && (
          <div className="px-4 pt-3 pb-2 border-b border-border/30">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-3 h-3 text-primary" />
              <span className="text-xs text-muted-foreground">Try an example:</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {EXAMPLE_PROMPTS.map((example, index) => (
                <Tooltip key={index}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs h-6 px-2.5 rounded-full bg-muted/50 hover:bg-muted transition-colors"
                      onClick={() => {
                        setInput(example.prompt)
                        toast.info(`Ready: ${example.label}`, {
                          description: 'Press Enter or click send to run this query.',
                          icon: <Wand2 className="w-4 h-4" />,
                        })
                        if (textareaRef.current) {
                          textareaRef.current.focus()
                          textareaRef.current.style.height = 'auto'
                          textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`
                        }
                      }}
                      disabled={disabled}
                    >
                      {example.label}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="text-xs">{example.tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </div>
        )}

        {/* Limit reached warning */}
        {isAtLimit && (
          <div className="px-4 py-3 border-b border-red-200 dark:border-red-800/50 bg-red-50/50 dark:bg-red-950/20 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-800 dark:text-red-300">
              Limit reached.{' '}
              <a
                href="https://docs.ecosemantic.com/connection/overview/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-red-700 dark:hover:text-red-400"
              >
                Connect to Claude/ChatGPT
              </a>{' '}
              to continue.
            </p>
          </div>
        )}

        <div className="flex gap-2 items-end p-3">
          <div className="flex-1">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              placeholder={isAtLimit ? 'Upgrade to continue...' : placeholder}
              disabled={disabled || isAtLimit}
              rows={1}
              className={cn(
                'w-full resize-none bg-transparent px-1 py-2',
                'focus:outline-none',
                'placeholder:text-muted-foreground/50',
                'disabled:cursor-not-allowed disabled:opacity-50',
                'transition-all duration-200',
                'min-h-[40px] max-h-[200px]',
                'text-sm',
              )}
              style={{
                height: 'auto',
                overflow: 'hidden',
                lineHeight: '1.5',
              }}
            />
          </div>

          {/* MCP Server Toggle */}
          <McpServerToggle />

          {/* Tools Selector */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setToolsOpen(true)}
            title="Select tools"
          >
            <Wrench className="w-4 h-4" />
          </Button>

          {/* Model Selector */}
          {models && models.length > 0 && (
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-10 px-2.5 shrink-0 text-xs text-muted-foreground hover:text-foreground gap-1"
                      disabled={disabled}
                    >
                      <span className="hidden sm:inline">
                        {currentModel?.display_name || 'Model'}
                      </span>
                      <span className="sm:hidden">
                        {currentModel?.display_name.split(' ').pop() || 'Model'}
                      </span>
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p className="text-xs">
                    {currentModel?.credit_cost} credit
                    {(currentModel?.credit_cost || 0) > 1 ? 's' : ''} per message
                  </p>
                </TooltipContent>
              </Tooltip>
              <DropdownMenuContent align="end" className="w-56">
                {models.map((model) => {
                  const isDisabled =
                    model.model_id.includes('opus') ||
                    model.model_id === 'anthropic/claude-sonnet-4.6'
                  return (
                    <DropdownMenuItem
                      key={model.model_id}
                      onClick={isDisabled ? undefined : () => onModelChange(model.model_id)}
                      disabled={isDisabled}
                      className={cn(
                        'flex items-center justify-between',
                        isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                        !isDisabled &&
                          model.model_id === (selectedModel || currentModel?.model_id) &&
                          'bg-accent',
                      )}
                    >
                      <span className="text-sm">{model.display_name}</span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        {isDisabled ? (
                          <span className="text-xs italic">enterprise</span>
                        ) : (
                          <>
                            <Coins className="w-3 h-3" />
                            {model.credit_cost}
                          </>
                        )}
                      </span>
                    </DropdownMenuItem>
                  )
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Button
            onClick={handleSubmit}
            disabled={disabled || !input.trim() || isAtLimit}
            size="icon"
            className={cn(
              'h-10 w-10 rounded-xl shrink-0',
              'transition-all duration-200',
              !disabled && input.trim() && !isAtLimit && 'hover:scale-105',
            )}
          >
            {disabled && !isAtLimit ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
      {catalog && (
        <ToolSelectorDialog
          open={toolsOpen}
          onOpenChange={setToolsOpen}
          catalog={catalog.tools}
          value={selectorValue}
          addOnly={addOnly}
          title={addOnly ? 'Add tools to this conversation' : 'Select tools for this conversation'}
          onSave={(enabled) => handleToolsSave(enabled)}
        />
      )}
    </div>
  )
}
